// The runner config's tabs, in their order on the row: web's ConfigView and
// mobile's RunnerSettings both draw this list. A module of its own so a URL
// reader (the web terminal window page) or a navigation param can check a tab
// name without pulling in the views.
const CONFIG_TABS = [
  'setup',
  'secrets',
  'skills',
  'deploy',
  'jobs',
  'access',
  'audit'
] as const;

export type ConfigTab = (typeof CONFIG_TABS)[number];

/** A tab named in a URL (`?tab=` on the terminal window, `?runnerTab=` on a
 *  board): anything else opens the default. */
export const asConfigTab = (value: string | null | undefined) =>
  CONFIG_TABS.find((tab) => tab === value);

const CONFIG_TAB_LABELS: Record<ConfigTab, string> = {
  setup: 'Setup',
  secrets: 'Secrets',
  skills: 'Skills',
  deploy: 'Deploy',
  jobs: 'Jobs',
  access: 'Access',
  audit: 'Audit'
};

/** Whether the viewer may see a tab on this runner. */
const TAB_SHOWN: Record<
  ConfigTab,
  (who: { isOwner: boolean; paired: boolean }) => boolean
> = {
  setup: () => true,
  secrets: () => true,
  // Runner functions, not board ones: any member on the space's runner uses
  // them, as they did from the board header.
  skills: () => true,
  deploy: () => true,
  jobs: () => true,
  // Owner only, and only for a paired runner: members reach it through the
  // server, a runner that reached you through a board is not yours to give
  // out, and the Electron-local agent has no row there.
  access: ({ isOwner, paired }) => isOwner && paired,
  audit: ({ isOwner }) => isOwner
};

/** The tabs this viewer gets, in row order. `isOwner`: the runner is the
 *  viewer's own, not one shared with them. `paired`: it is a paired runner
 *  (has a server row), not the desktop app's local agent. */
export const configTabsFor = (who: { isOwner: boolean; paired: boolean }) =>
  CONFIG_TABS.filter((id) => TAB_SHOWN[id](who)).map((id) => ({
    id,
    label: CONFIG_TAB_LABELS[id]
  }));
